import {
  BIG_ONE,
  BIG_TWO,
  BIG_ZERO,
  modPowBig,
} from "@/lib/tool-kit/math/bigint";

/**
 * Primality for the Prime Number Checker.
 *
 * Small numbers are settled by trial division by the first 25 primes. Everything else goes
 * through the Miller–Rabin test with bases chosen from the published thresholds (OEIS
 * A014233): testing the first k primes as bases is a proof of primality for every n below
 * the k-th threshold. Past the 13th threshold (about 3.3 × 10^24) no fixed small set of
 * bases is proven, so the verdict is "probable prime" and says so. A composite verdict is
 * always a proof, because a Miller–Rabin witness is a certificate.
 */

export const MAX_DIGITS = 200;
/** The input range for "primes between a and b" (a Number-safe sieve). */
export const MAX_RANGE_END = 1_000_000_000_000;
export const MAX_RANGE_WIDTH = 2_000_000;

const SMALL_PRIMES = [
  2, 3, 5, 7, 11, 13, 17, 19, 23, 29, 31, 37, 41, 43, 47, 53, 59, 61, 67, 71,
  73, 79, 83, 89, 97,
].map((p) => BigInt(p));

/**
 * `limits[k - 1]` is the smallest odd composite that passes Miller–Rabin for the first k
 * prime bases (OEIS A014233), so k bases prove primality below it.
 */
const BASE_LIMITS = [
  "2047",
  "1373653",
  "25326001",
  "3215031751",
  "2152302898747",
  "3474749660383",
  "341550071728321",
  "341550071728321",
  "3825123056546413051",
  "3825123056546413051",
  "3825123056546413051",
  "318665857834031151167461",
  "3317044064679887385961981",
].map((s) => BigInt(s));

/** Bases used above the proven range: the first 20 primes. */
const PROBABLE_BASE_COUNT = 20;

/** The bound below which the first `count` prime bases are a proof (count 1 to 13). */
export function provenLimit(count: number): bigint | null {
  return BASE_LIMITS[count - 1] ?? null;
}

export interface BaseChoice {
  bases: bigint[];
  /** True when these bases are a proof for this n. */
  proven: boolean;
}

/** The fewest prime bases that make the test a proof for n, or 20 bases if none do. */
export function chooseBases(n: bigint): BaseChoice {
  const index = BASE_LIMITS.findIndex((limit) => n < limit);
  if (index === -1) {
    return {
      bases: SMALL_PRIMES.slice(0, PROBABLE_BASE_COUNT),
      proven: false,
    };
  }
  return { bases: SMALL_PRIMES.slice(0, index + 1), proven: true };
}

export interface MillerRabinDecomposition {
  /** n − 1 = 2^s × d with d odd. */
  s: number;
  d: bigint;
}

export function decompose(n: bigint): MillerRabinDecomposition {
  let d = n - BIG_ONE;
  let s = 0;
  while (d % BIG_TWO === BIG_ZERO) {
    d /= BIG_TWO;
    s += 1;
  }
  return { s, d };
}

export type RoundOutcome = "one" | "minus-one" | "witness";

export interface MillerRabinRound {
  base: bigint;
  /** a^d, then each squaring, mod n — stopping where the verdict is decided. */
  sequence: bigint[];
  outcome: RoundOutcome;
}

/** One base of the test, with the values it passes through. */
export function millerRabinRound(
  n: bigint,
  base: bigint,
  { s, d }: MillerRabinDecomposition
): MillerRabinRound {
  const minusOne = n - BIG_ONE;
  let x = modPowBig(base, d, n);
  const sequence = [x];
  if (x === BIG_ONE) {
    return { base, sequence, outcome: "one" };
  }
  if (x === minusOne) {
    return { base, sequence, outcome: "minus-one" };
  }
  for (let round = 1; round < s; round += 1) {
    x = (x * x) % n;
    sequence.push(x);
    if (x === minusOne) {
      return { base, sequence, outcome: "minus-one" };
    }
    if (x === BIG_ONE) {
      // A square root of 1 other than ±1: n cannot be prime.
      return { base, sequence, outcome: "witness" };
    }
  }
  return { base, sequence, outcome: "witness" };
}

export type Verdict = "prime" | "probable-prime" | "composite" | "neither";

export interface PrimalityResult {
  verdict: Verdict;
  /** The small prime that divides n, when trial division found one. */
  smallFactor: bigint | null;
  decomposition: MillerRabinDecomposition | null;
  rounds: MillerRabinRound[];
  proven: boolean;
}

/** Decide primality for a whole number n ≥ 0, keeping the Miller–Rabin working. */
export function testPrimality(n: bigint): PrimalityResult {
  const none = {
    decomposition: null,
    rounds: [] as MillerRabinRound[],
    proven: true,
  };
  if (n < BIG_TWO) {
    return { verdict: "neither", smallFactor: null, ...none };
  }
  for (const p of SMALL_PRIMES) {
    if (n === p) {
      return { verdict: "prime", smallFactor: null, ...none };
    }
    if (n % p === BIG_ZERO) {
      return { verdict: "composite", smallFactor: p, ...none };
    }
  }
  const decomposition = decompose(n);
  const { bases, proven } = chooseBases(n);
  const rounds: MillerRabinRound[] = [];
  for (const base of bases) {
    const round = millerRabinRound(n, base, decomposition);
    rounds.push(round);
    if (round.outcome === "witness") {
      return {
        verdict: "composite",
        smallFactor: null,
        decomposition,
        rounds,
        proven: true,
      };
    }
  }
  return {
    verdict: proven ? "prime" : "probable-prime",
    smallFactor: null,
    decomposition,
    rounds,
    proven,
  };
}

/** True when n is prime — or, above 3.3 × 10^24, a probable prime. */
export function isPrimeFast(n: bigint): boolean {
  const { verdict } = testPrimality(n);
  return verdict === "prime" || verdict === "probable-prime";
}

const MAX_SEARCH_STEPS = 200_000;

/** The smallest prime greater than n, or null if the search gives up. */
export function nextPrime(n: bigint): bigint | null {
  if (n < BIG_TWO) {
    return BIG_TWO;
  }
  let candidate = n + BIG_ONE;
  if (candidate % BIG_TWO === BIG_ZERO) {
    candidate += BIG_ONE;
  }
  for (let step = 0; step < MAX_SEARCH_STEPS; step += 1) {
    if (isPrimeFast(candidate)) {
      return candidate;
    }
    candidate += BIG_TWO;
  }
  return null;
}

/** The largest prime less than n, or null when there is none (n ≤ 2). */
export function previousPrime(n: bigint): bigint | null {
  if (n <= BIG_TWO) {
    return null;
  }
  if (n === BigInt(3)) {
    return BIG_TWO;
  }
  let candidate = n - BIG_ONE;
  if (candidate % BIG_TWO === BIG_ZERO) {
    candidate -= BIG_ONE;
  }
  for (
    let step = 0;
    step < MAX_SEARCH_STEPS && candidate > BIG_TWO;
    step += 1
  ) {
    if (isPrimeFast(candidate)) {
      return candidate;
    }
    candidate -= BIG_TWO;
  }
  return candidate === BIG_TWO ? BIG_TWO : null;
}

/* ------------------------------------------------------------- reading */

export type ParsedNumber =
  | { kind: "ok"; value: bigint }
  | { kind: "empty" | "invalid" | "negative" | "too-large" };

const SEPARATORS = /[\s_  ]+/g;
const PLAIN = /^\d+$/;
const POWER = /^(\d+)\^(\d+)(?:([+\-−])(\d+))?$/;

/**
 * A whole number, or a short power expression — `2^127-1`, `2^16+1` — so Mersenne and
 * Fermat numbers can be typed. Spaces and underscores between digit groups are ignored.
 */
export function parseCandidate(raw: string): ParsedNumber {
  const text = raw.replace(SEPARATORS, "");
  if (text === "") {
    return { kind: "empty" };
  }
  if (text.startsWith("-") || text.startsWith("−")) {
    return PLAIN.test(text.slice(1))
      ? { kind: "negative" }
      : { kind: "invalid" };
  }
  if (PLAIN.test(text)) {
    return text.length > MAX_DIGITS
      ? { kind: "too-large" }
      : { kind: "ok", value: BigInt(text) };
  }
  const match = POWER.exec(text);
  if (match === null) {
    return { kind: "invalid" };
  }
  const base = BigInt(match[1]);
  const exponent = Number(match[2]);
  if (!Number.isSafeInteger(exponent)) {
    return { kind: "too-large" };
  }
  const digits = base <= BIG_ONE ? 1 : exponent * Math.log10(Number(match[1]));
  if (digits > MAX_DIGITS || (match[4] && match[4].length > MAX_DIGITS)) {
    return { kind: "too-large" };
  }
  let value = base ** BigInt(exponent);
  if (match[3] && match[4]) {
    const offset = BigInt(match[4]);
    value = match[3] === "+" ? value + offset : value - offset;
  }
  if (value < BIG_ZERO) {
    return { kind: "negative" };
  }
  return value.toString().length > MAX_DIGITS
    ? { kind: "too-large" }
    : { kind: "ok", value };
}

/* --------------------------------------------------------------- sieve */

function smallSieve(limit: number): number[] {
  const composite = new Uint8Array(limit + 1);
  const primes: number[] = [];
  for (let i = 2; i <= limit; i += 1) {
    if (composite[i] === 0) {
      primes.push(i);
      for (let j = i * i; j <= limit; j += i) {
        composite[j] = 1;
      }
    }
  }
  return primes;
}

/** Primes in [lo, hi] by a segmented sieve of Eratosthenes. Needs hi ≤ 10^12. */
export function primesInRange(lo: number, hi: number): number[] {
  const start = Math.max(lo, 2);
  if (hi < start) {
    return [];
  }
  const root = Math.floor(Math.sqrt(hi));
  const composite = new Uint8Array(hi - start + 1);
  for (const p of smallSieve(root)) {
    const first = Math.max(p * p, Math.ceil(start / p) * p);
    for (let multiple = first; multiple <= hi; multiple += p) {
      composite[multiple - start] = 1;
    }
  }
  const out: number[] = [];
  for (let i = 0; i < composite.length; i += 1) {
    if (composite[i] === 0) {
      out.push(start + i);
    }
  }
  return out;
}

export interface RangeStats {
  count: number;
  first: number | null;
  last: number | null;
  largestGap: { from: number; to: number; gap: number } | null;
  twinPairs: number;
}

export function rangeStats(primes: readonly number[]): RangeStats {
  let largestGap: RangeStats["largestGap"] = null;
  let twinPairs = 0;
  for (let i = 1; i < primes.length; i += 1) {
    const gap = primes[i] - primes[i - 1];
    if (gap === 2) {
      twinPairs += 1;
    }
    if (largestGap === null || gap > largestGap.gap) {
      largestGap = { from: primes[i - 1], to: primes[i], gap };
    }
  }
  return {
    count: primes.length,
    first: primes[0] ?? null,
    last: primes.at(-1) ?? null,
    largestGap,
    twinPairs,
  };
}

/* ------------------------------------------------------------ the range */

export type RangeRead =
  | { kind: "ok"; lo: number; hi: number }
  | { kind: "empty" | "invalid" | "reversed" | "too-large" | "too-wide" };

function readBound(raw: string): number | "empty" | "invalid" | "too-large" {
  const text = raw.replace(SEPARATORS, "");
  if (text === "") {
    return "empty";
  }
  if (!PLAIN.test(text)) {
    return "invalid";
  }
  const value = Number(text);
  return value > MAX_RANGE_END ? "too-large" : value;
}

/** The two ends of a "primes between" request, checked against the sieve's limits. */
export function readRange(rawLo: string, rawHi: string): RangeRead {
  const lo = readBound(rawLo);
  const hi = readBound(rawHi);
  for (const bound of [lo, hi]) {
    if (typeof bound === "string") {
      return { kind: bound };
    }
  }
  if (typeof lo !== "number" || typeof hi !== "number") {
    return { kind: "invalid" };
  }
  if (hi < lo) {
    return { kind: "reversed" };
  }
  if (hi - lo > MAX_RANGE_WIDTH) {
    return { kind: "too-wide" };
  }
  return { kind: "ok", lo, hi };
}
